import { formatDisplayDate } from "./displayFormat.mjs";
export const DEFAULT_COMPANY_LICENSE_PACKAGE_KEY = "hr-core";

const SUPPORTED_LOCALES = new Set(["ro", "en", "ru"]);
const KNOWN_LICENSE_PACKAGE_LABELS = {
  "hr-core": {
    ro: "Administrare personal",
    en: "Personnel administration",
    ru: "Управление персоналом",
  },
  "hr-crm": {
    ro: "Administrare personal + CRM",
    en: "Personnel administration + CRM",
    ru: "Управление персоналом + CRM",
  },
};

const normalizeLocale = (value) => {
  const normalized = value ? String(value).trim().toLowerCase() : "ro";
  return SUPPORTED_LOCALES.has(normalized) ? normalized : "ro";
};

const normalizePackageKey = (value) => String(value || "").trim().toLowerCase();

const normalizeBooleanFlag = (value, fallback = 0) => {
  if (value === null || value === undefined || value === "") {
    return fallback ? 1 : 0;
  }
  return Number(value) === 1 || value === true ? 1 : 0;
};

const normalizeStatusValue = (value, fallback = 1) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }
  return Number(value) === 0 ? 0 : 1;
};

export const normalizeCompanyLicenseDateInput = (value) => {
  if (!value) return "";
  const asString = String(value).trim();
  if (!asString) return "";
  const directMatch = asString.match(/^(\d{4}-\d{2}-\d{2})/);
  if (directMatch) {
    return directMatch[1];
  }
  const parsed = new Date(asString);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }
  return parsed.toISOString().slice(0, 10);
};

export const resolveLicensePackageLabel = (pkg, locale = "ro") => {
  if (!pkg || typeof pkg !== "object") return null;
  const normalizedLocale = normalizeLocale(locale);
  return (
    pkg[`label_${normalizedLocale}`] ||
    pkg.label_ro ||
    pkg.label_en ||
    pkg.label_ru ||
    pkg.label ||
    pkg.key ||
    null
  );
};

export const resolveLicensePackageLabelByKey = (
  packages = [],
  packageKey = DEFAULT_COMPANY_LICENSE_PACKAGE_KEY,
  locale = "ro"
) => {
  const normalizedKey = normalizePackageKey(packageKey || DEFAULT_COMPANY_LICENSE_PACKAGE_KEY);
  if (!normalizedKey) return null;
  const matchedPackage = (Array.isArray(packages) ? packages : []).find(
    (pkg) => normalizePackageKey(pkg?.key) === normalizedKey
  );
  const normalizedLocale = normalizeLocale(locale);
  return (
    resolveLicensePackageLabel(matchedPackage, normalizedLocale) ||
    KNOWN_LICENSE_PACKAGE_LABELS?.[normalizedKey]?.[normalizedLocale] ||
    KNOWN_LICENSE_PACKAGE_LABELS?.[normalizedKey]?.ro ||
    normalizedKey
  );
};

export const buildLicensePackageSelectItems = (packages = [], locale = "ro") =>
  (Array.isArray(packages) ? packages : [])
    .map((pkg) => ({
      title: resolveLicensePackageLabelByKey(packages, pkg?.key, locale) || String(pkg?.key || "").trim(),
      value: normalizePackageKey(pkg?.key),
    }))
    .filter((pkg) => pkg.title && pkg.value);

export const createEmptyCompanyForm = ({ packageKey = DEFAULT_COMPANY_LICENSE_PACKAGE_KEY } = {}) => ({
  id: null,
  company_name: "",
  administrator: "",
  fiscal_code: "",
  vat_number: "",
  phone: "",
  email: "",
  address: "",
  postal_code: "",
  iban: "",
  contact_person: "",
  status: 1,
  pdfrequest: 0,
  email_notifications_disabled: 0,
  license_package_key: normalizePackageKey(packageKey || DEFAULT_COMPANY_LICENSE_PACKAGE_KEY),
  license_expires_at: "",
});

export const buildCompanyForm = (
  company = {},
  license = null,
  { defaultPackageKey = DEFAULT_COMPANY_LICENSE_PACKAGE_KEY } = {}
) => {
  const base = createEmptyCompanyForm({ packageKey: defaultPackageKey });
  return {
    ...base,
    id: company?.id ?? base.id,
    company_name: company?.company_name || company?.companyName || base.company_name,
    administrator: company?.administrator || base.administrator,
    fiscal_code: company?.fiscal_code || base.fiscal_code,
    vat_number: company?.vat_number || base.vat_number,
    phone: company?.phone || base.phone,
    email: company?.email || base.email,
    address: company?.address || base.address,
    postal_code: company?.postal_code || base.postal_code,
    iban: company?.iban || base.iban,
    contact_person: company?.contact_person || base.contact_person,
    status: normalizeStatusValue(company?.status, base.status),
    pdfrequest: normalizeBooleanFlag(company?.pdfrequest, base.pdfrequest),
    email_notifications_disabled: normalizeBooleanFlag(
      company?.email_notifications_disabled,
      base.email_notifications_disabled
    ),
    license_package_key: normalizePackageKey(
      license?.package_key || company?.license_package_key || base.license_package_key
    ),
    license_expires_at: normalizeCompanyLicenseDateInput(
      license?.expires_at || company?.license_expires_at || base.license_expires_at
    ),
  };
};

export const buildCompanyPayload = (form = {}) => ({
  company_name: form.company_name || null,
  administrator: form.administrator || null,
  fiscal_code: form.fiscal_code || null,
  vat_number: form.vat_number || null,
  phone: form.phone || null,
  email: form.email || null,
  address: form.address || null,
  postal_code: form.postal_code || null,
  iban: form.iban || null,
  contact_person: form.contact_person || null,
  status: normalizeStatusValue(form.status, 1),
  pdfrequest: normalizeBooleanFlag(form.pdfrequest, 0),
  email_notifications_disabled: normalizeBooleanFlag(form.email_notifications_disabled, 0),
});

export const buildLicensePayload = (
  form = {},
  { defaultPackageKey = DEFAULT_COMPANY_LICENSE_PACKAGE_KEY } = {}
) => {
  const expiresDate = normalizeCompanyLicenseDateInput(form.license_expires_at);
  return {
    package_key: String(form.license_package_key || defaultPackageKey || DEFAULT_COMPANY_LICENSE_PACKAGE_KEY)
      .trim()
      .toLowerCase(),
    status: "active",
    expires_at: expiresDate ? `${expiresDate} 23:59:59` : null,
  };
};

export const formatCompanyLicenseExpiry = (value, locale = "ro", fallback = "") => {
  const normalizedDate = normalizeCompanyLicenseDateInput(value);
  if (!normalizedDate) return fallback;
  const parsed = new Date(`${normalizedDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return normalizedDate;
  }
  return formatDisplayDate(parsed);
};
